import type { AssetDationItem } from '../../../../constants/payment';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// ─── Operações ──────────────────────────────────────────────────────────────

/** Uma operação como o settlement a vê: o do credit + o que os títulos dizem sobre ela. */
export interface SettlementOperation {
    credit_operation_id: string;
    sequencial_number: number;
    amendment_number?: number;
    contract_label?: string;
    operation_number?: string | null;
    operation_type: string;
    credit_product_type_name?: string;
    /** emitente/cedente já resolvido na view (ASSIGNOR/BORROWER preferencial) */
    issuer_name?: string | null;
    issuer_document?: string | null;
    issuer_type?: string | null;
    /** soma dos title_debt da operação — o que ela DEVE. NULL quando os títulos não chegaram. */
    amount?: number | null;
    paid_amount?: number | null;
    /** do credit; não responde pelo que a operação deve — ver AGENTS.md */
    nominal_value?: number | null;
    /** o DESEMBOLSADO (disbursement.totalAmount); não existe mais "valor aprovado" aqui */
    disbursement_value?: number | null;
    expiration_date?: string | null;
    business_reference?: string | null;
    title_debts_count?: number;
    status?: string;
}

/**
 * Busca de operações para as telas de cobrança.
 *
 * Vem do settlement e não do contracts_management de propósito: aqui a operação interessa pelo que
 * ela DEVE (`amount`, soma dos títulos) e pelo que já foi recebido (`paid_amount`), e o
 * contracts_management só conhece o `nominal_value` do credit.
 *
 * `status = 'Finalizado com sucesso'` e `billed IS TRUE` já são chumbados no servidor — não
 * precisa mandar como filtro fixo.
 */
export const listOperations = async (
    params: Record<string, string>,
): Promise<{ list: SettlementOperation[]; totalElements: number; totalPages: number }> => {
    // URLSearchParams codifica espaço como "+", e o API Gateway não converte de volta
    const qs = new URLSearchParams(params).toString().replace(/\+/g, '%20');
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/operations?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
        list: data.list || [],
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
    };
};

// ─── Title Debts ────────────────────────────────────────────────────────────

export const listTitleDebts = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtDetail = async (id: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtMovements = async (id: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}/movements`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

/**
 * Histórico de movimentações de TODOS os títulos de uma operação (AF-3400).
 * Mesmo formato de getTitleDebtMovements; cada movimentação diz de qual título veio.
 * 404 quando a operação não tem título nenhum.
 */
export const getOperationMovements = async (creditOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/operations/${creditOperationId}/movements`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Payments (Baixas) ──────────────────────────────────────────────────────

export interface RegisterPaymentPayload {
    amount: number;
    payment_date?: string;  // instante ISO (UTC) da baixa
    payment_type: string;
    payment_method?: string;
    settlement_type: string;
    bank_account?: string;                 // alias (compat/exibição)
    receiving_bank_account_id?: string;    // conta de recebimento (alias ou uuid) — canônico
    payment_source?: string;
    pending_guarantor_refund?: boolean;
    notes?: string;
    debt_confession_contract?: string;
    /** UM bem, não uma lista — ver `paymentPayload` e o schema do billing */
    asset_dation?: AssetDationItem | null;
    /** pagador informado na tela — sobrepõe o payer do título quando presente */
    payer?: {
        external_id?: string;
        name: string;
        document_number: string;
        email?: string;
        phone?: string;
        address_street?: string;
        address_number?: string;
        address_complement?: string;
        address_neighborhood?: string;
        address_city?: string;
        address_state?: string;
        address_zip_code?: string;
    };
    file_name_receipt?: string;
    file_url_receipt?: string;
    user_responsible?: string;
}

export const registerPayment = async (titleDebtId: number | string, payload: RegisterPaymentPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// TODO: Endpoint batch-payments (AF-2729 Fase 3)
export interface BatchPaymentItem {
    title_debt_id: number;
    amount: number;
    payment_method: string;
    settlement_type: string;
    bank_account?: string;
    receiving_bank_account_id?: string;
    payment_source?: string;
    notes?: string;
}

export const registerBatchPayments = async (payments: BatchPaymentItem[]) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/batch-payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ payments }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Reversals ──────────────────────────────────────────────────────────────

export interface ReversalPayload {
    reasons: string[];
    notes?: string;
}

export const registerReversal = async (titleDebtId: number | string, payload: ReversalPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/reversals`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// ─── Invoices ───────────────────────────────────────────────────────────────

export const listInvoices = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Simulations (TODO: settlement-simulation-api) ──────────────────────────

export interface PaymentSimulationPayload {
    contract_operation_id: string;
    amount: number;
    payment_date: string;
    payment_source?: string;
}

export const simulatePayment = async (payload: PaymentSimulationPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityBuckets = async (contractOperationId?: string) => {
    const headers = await getHeaders();
    const qs = contractOperationId ? `?contract_operation_id=${contractOperationId}` : '';
    const res = await fetch(`${API_URL}/maturities/buckets${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityProjections = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/maturities/projections?contract_operation_id=${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const simulateEarlyPayment = async (titleDebtId: number | string, paymentDate: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/early-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title_debt_id: titleDebtId, payment_date: paymentDate }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getFinancialMetrics = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/financials/${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};
